import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image, Camera, ChevronRight, ChevronLeft, X, Grid, 
  LayoutGrid, Filter, Sparkles, ZoomIn, Download, Share2,
  Folder, Images, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BROKEN_IMG = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%231c1917"/><g fill="%2357534e"><circle cx="200" cy="130" r="34" fill="none" stroke="%2357534e" stroke-width="6"/><rect x="150" y="90" width="100" height="14" rx="6"/><text x="200" y="220" font-family="sans-serif" font-size="16" text-anchor="middle" fill="%2378716c">Photo unavailable</text></g></svg>';

const handleImgError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = BROKEN_IMG;
};

const getVideoEmbed = (url) => {
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-z0-9]+))?/i);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1${vm[2] ? `&h=${vm[2]}` : ''}`;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{6,})/i);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1`;
  return null;
};

const VideoCard = ({ video, index }) => {
  const [playing, setPlaying] = React.useState(false);
  const embed = getVideoEmbed(video.url);
  return (
    <div className="relative aspect-video rounded-xl overflow-hidden bg-black border border-border/50" data-testid={`album-video-${index}`}>
      {playing ? (
        embed ? (
          <iframe src={embed} className="absolute inset-0 w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title={video.title || `Video ${index + 1}`} />
        ) : (
          <video src={video.url} className="absolute inset-0 w-full h-full" controls autoPlay />
        )
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-stone-900 to-stone-800 hover:from-stone-800 hover:to-stone-700 transition-colors"
          data-testid={`album-video-play-${index}`}
        >
          <span className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
            <svg viewBox="0 0 24 24" className="w-6 h-6 ml-1 text-black" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
          </span>
          <span className="text-sm text-white font-medium px-4 text-center">{video.title || 'Play Video'}</span>
        </button>
      )}
    </div>
  );
};

export default function Gallery() {
  return (
    <PageEditProvider slug="gallery">
      <GalleryInner />
      <PageEditToolbar />
      <AdminEditorLink to="/admin/dashboard#gallery-admin-btn" label="Edit Gallery" />
    </PageEditProvider>
  );
}

function GalleryInner() {
  const hero = useHero('gallery');
  const { albumSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [albums, setAlbums] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    loadData();
  }, [activeCategory, albumSlug]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // If viewing a specific album, redirect to the new gc viewer (data lives in gc_galleries now)
      if (albumSlug) {
        navigate(`/g/${albumSlug}`, { replace: true });
        return;
      } else {
        setSelectedAlbum(null);
        const [gcRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/gc/galleries`),
          axios.get(`${API}/gallery/categories`).catch(() => ({ data: [] }))
        ]);
        const items = (gcRes.data?.items || []).map((g) => ({
          id: g.id,
          slug: g.slug,
          name: g.title,
          description: g.description || '',
          cover_image: g.cover_image || '',
          image_count: g.album_count || 0,
          is_featured: false,
          category: g.category || 'all',
          _source: 'gc',
        }));
        setAlbums(items);
        setCategories(categoriesRes.data || []);
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setSearchParams(cat !== 'all' ? { category: cat } : {});
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = useCallback(() => {
    if (selectedAlbum?.images) {
      setLightboxIndex((prev) => (prev + 1) % selectedAlbum.images.length);
    }
  }, [selectedAlbum]);

  const prevImage = useCallback(() => {
    if (selectedAlbum?.images) {
      setLightboxIndex((prev) => (prev - 1 + selectedAlbum.images.length) % selectedAlbum.images.length);
    }
  }, [selectedAlbum]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextImage, prevImage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="gallery-loading">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Single Album View
  if (selectedAlbum) {
    return (
      <div className="min-h-screen pb-20" data-testid="album-view">
        {/* Album Header */}
        <section className="relative py-16 px-6 bg-gradient-to-b from-stone-900 to-background">
          <div className="max-w-7xl mx-auto">
            <Link 
              to="/gallery" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Galleries
            </Link>
            
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <span className="text-primary font-bold uppercase tracking-wider text-sm">
                  {categories.find(c => c.id === selectedAlbum.category)?.name || selectedAlbum.category}
                </span>
                <h1 className="text-3xl md:text-5xl font-bold font-display text-foreground mt-2" data-testid="album-title">
                  {selectedAlbum.name}
                </h1>
                {selectedAlbum.description && (
                  <p className="text-muted-foreground mt-3 max-w-2xl">{selectedAlbum.description}</p>
                )}
              </div>
              <div className="flex items-center gap-4 text-muted-foreground">
                <span className="flex items-center gap-2"><Images className="w-5 h-5" />{selectedAlbum.images?.length || 0} photos</span>
                {selectedAlbum.videos?.length > 0 && (
                  <span className="flex items-center gap-2"><Camera className="w-5 h-5" />{selectedAlbum.videos.length} videos</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Videos Section */}
        {selectedAlbum.videos?.length > 0 && (
          <section className="px-6 pt-2 pb-4">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-bold text-foreground mb-4" data-testid="album-videos-title">Videos</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="album-videos-grid">
                {selectedAlbum.videos.map((v, idx) => (
                  <VideoCard key={idx} video={v} index={idx} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Photo Grid */}
        <section className="px-6 py-12">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4" data-testid="photo-grid">
              {selectedAlbum.images?.map((image, idx) => {
                // Handle both string URLs and object format
                const imageUrl = typeof image === 'string' ? image : (image.thumbnail || image.url);
                const imageAlt = typeof image === 'string' ? `Photo ${idx + 1}` : (image.title || `Photo ${idx + 1}`);
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.02 }}
                    className="relative aspect-[4/3] group cursor-pointer overflow-hidden rounded-lg bg-stone-800"
                    onClick={() => openLightbox(idx)}
                    data-testid={`photo-${idx}`}
                  >
                    <img
                      src={imageUrl}
                      alt={imageAlt}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={handleImgError}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center">
                      <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        <AnimatePresence>
          {lightboxOpen && selectedAlbum.images && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black flex items-center justify-center"
              onClick={closeLightbox}
              data-testid="lightbox"
            >
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                data-testid="lightbox-close"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Navigation */}
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-8 h-8" />
              </button>

              {/* Image */}
              {(() => {
                const currentImage = selectedAlbum.images[lightboxIndex];
                const imgUrl = typeof currentImage === 'string' ? currentImage : currentImage?.url;
                const imgAlt = typeof currentImage === 'string' ? `Photo ${lightboxIndex + 1}` : (currentImage?.title || `Photo ${lightboxIndex + 1}`);
                return (
                  <motion.img
                    key={lightboxIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    src={imgUrl}
                    alt={imgAlt}
                    className="max-w-[90vw] max-h-[85vh] object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                );
              })()}

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 rounded-full text-white text-sm">
                {lightboxIndex + 1} / {selectedAlbum.images.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Gallery Overview (All Albums)
  return (
    <div className="min-h-screen pb-20" data-testid="gallery-page">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-background to-yellow-900/10" />
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 right-20 w-72 h-72 bg-green-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-yellow-600/15 rounded-full blur-3xl" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-7xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/20 border border-primary/30 mb-6">
            <Camera className="w-5 h-5 text-primary" />
            <span className="text-sm font-bold text-primary uppercase tracking-wider">
              <PageEditable path="hero.kicker" as="span" fallback="38 Years of Memories" />
            </span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-4" data-testid="gallery-title">
            <PageEditable path="hero.titlePre" as="span" fallback={hero.title_top || "Photo & Video"} /> <span className="text-primary"><PageEditable path="hero.titleAccent" as="span" fallback={hero.title_bottom || "Gallery"} /></span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            <PageEditable
              path="hero.subtitle"
              as="span"
              multiline
              fallback="Explore decades of performances, parades, classes, and celebrations. The visual history of Escola de Samba Casa Samba's Afro-Brasilian cultural journey."
            />
          </p>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-stone-800 text-muted-foreground hover:bg-stone-700 hover:text-foreground'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Albums Grid */}
        {albums.length === 0 ? (
          <div className="text-center py-20" data-testid="no-albums">
            <Folder className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold text-foreground mb-2">No Albums Found</h3>
            <p className="text-muted-foreground">Check back soon for new photo galleries!</p>
          </div>
        ) : (
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            data-testid="albums-grid"
          >
            {albums.map((album, idx) => (
              <motion.div
                key={album.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                layout
              >
                <Link
                  to={album._source === 'gc' ? `/g/${album.slug}` : `/gallery/${album.slug}`}
                  className="group block overflow-hidden bg-card border border-border/50 rounded-2xl hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                  data-testid={`album-card-${album.id}`}
                >
                  {/* Cover Image */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-stone-800">
                    <img
                      src={album.cover_image}
                      alt={album.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                      onError={handleImgError}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    
                    {/* Featured Badge */}
                    {album.is_featured && (
                      <div className="absolute top-3 left-3 flex items-center gap-1 px-3 py-1 bg-primary text-primary-foreground rounded-full text-xs font-bold">
                        <Sparkles className="w-3 h-3" />
                        Featured
                      </div>
                    )}

                    {/* Image Count */}
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm">
                      <Images className="w-4 h-4" />
                      {album.image_count || album.images?.length || 0}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                      {categories.find(c => c.id === album.category)?.name || album.category}
                    </span>
                    <h3 className="text-lg font-bold text-foreground mt-1 group-hover:text-primary transition-colors line-clamp-1">
                      {album.name}
                    </h3>
                    {album.description && (
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {album.description}
                      </p>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}

